import React, { FormEvent, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CsvCondition } from 'config/csv';
import { monthBounds } from 'config/time';
import { ExportField, exportRowsToCsv } from 'reusable/export';
import { TooManyRowsError } from 'reusable/fetchAllPages';
import { formatMonthLabel } from 'reusable/MonthNavigator';
import { inModalLayer, useModalDialog } from 'reusable/useModalDialog';

/** 出す範囲。業務日（`YYYY-MM-DD`）の両端を含む。null は全件。 */
export type ExportRange = { start: string; end: string } | null;

type RangeMode = 'month' | 'period' | 'all';

interface ExportRangeDialogProps {
  /** 一覧で表示している月（`YYYY-MM`）。「この月」の範囲と「期間を指定」の初期値。 */
  month: string;
  exportFields: ExportField[];
  /** ADM-11-AC2。ファイル名の先頭（画面ごとの名前）。 */
  name: string;
  /** 範囲のほかの条件（運転者・並び順・絞り込み）。範囲はここで足す。 */
  conditions?: CsvCondition[];
  /** 範囲の記録を全ページ読んで返す（reusable/fetchAllPages）。 */
  fetchRows: (range: ExportRange) => Promise<any[]>;
}

/**
 * A28・A36（2026-11-03 和田様の UAT）。エクスポートの範囲を「この月／期間を指定／全件」から選ぶ。
 *
 * 以前のエクスポートは表示中の1ページ分（10件）だけだった。選んだ範囲の記録を一覧の API から
 * 全ページ読み、今までと同じ列・同じ書式（reusable/export の exportRowsToCsv）で CSV にする。
 * 件数が多すぎるとき（MAX_EXPORT_ROWS を超える）・読めなかったときは、ダイアログに理由を出して残す。
 */
const ExportRangeDialog: React.FC<ExportRangeDialogProps> = ({
  month,
  exportFields,
  name,
  conditions = [],
  fetchRows,
}) => {
  const { t, i18n } = useTranslation('common');
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<RangeMode>('month');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const headingId = useId();
  const groupName = useId();
  const startId = useId();
  const endId = useId();
  const close = () => {
    if (!busy) setIsOpen(false);
  };
  const dialogRef = useModalDialog(isOpen, close);

  const open = () => {
    const bounds = monthBounds(month);
    setMode('month');
    setStart(bounds.start);
    setEnd(bounds.end);
    setError('');
    setIsOpen(true);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    let range: ExportRange;
    let period: string;
    if (mode === 'month') {
      range = monthBounds(month);
      period = month;
    } else if (mode === 'period') {
      if (!start || !end) {
        setError(t('export-range.period-required'));
        return;
      }
      if (start > end) {
        setError(t('export-range.period-order'));
        return;
      }
      range = { start, end };
      period = `${start}_${end}`;
    } else {
      range = null;
      period = 'all';
    }
    setBusy(true);
    setError('');
    try {
      const rows = await fetchRows(range);
      exportRowsToCsv(rows, exportFields, name, [['period', period], ...conditions]);
      setIsOpen(false);
    } catch (err) {
      console.error('Failed to export', err);
      setError(
        err instanceof TooManyRowsError
          ? t('export-range.too-many', { count: err.count, max: err.max })
          : t('export-range.failed')
      );
    } finally {
      setBusy(false);
    }
  };

  const option = (value: RangeMode, label: string) => (
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="radio"
        name={groupName}
        value={value}
        checked={mode === value}
        onChange={() => {
          setMode(value);
          setError('');
        }}
      />
      <span>{label}</span>
    </label>
  );

  return (
    <>
      <button
        type="button"
        onClick={open}
        className="bg-primary/50 text-primary/300 rounded-md border px-4 h-10 text-center whitespace-nowrap"
      >
        {t('export')}
      </button>
      {isOpen &&
        inModalLayer(
          <div className="fixed z-10 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4">
              <div className="fixed inset-0 bg-gray-500 opacity-75" aria-hidden="true" />
              <div
                className="relative bg-white rounded-lg text-left shadow-xl w-full max-w-md"
                role="dialog"
                aria-modal="true"
                aria-labelledby={headingId}
                ref={dialogRef}
                tabIndex={-1}
              >
                <form onSubmit={handleSubmit}>
                  <h3 id={headingId} className="text-subtitle-1 font-medium text-neutral/500 mx-6 mt-4">
                    {t('export-range.title')}
                  </h3>
                  <hr className="border-neutral/50 mt-4 w-full" />
                  <fieldset className="flex flex-col gap-3 m-6" disabled={busy}>
                    <legend className="mb-2 text-body-2 text-neutral/300">{t('export-range.legend')}</legend>
                    {option('month', t('export-range.month', { month: formatMonthLabel(month, i18n.language) }))}
                    {option('period', t('export-range.period'))}
                    {mode === 'period' && (
                      <div className="flex flex-row flex-wrap items-center gap-2 pl-6">
                        <label htmlFor={startId} className="sr-only">
                          {t('export-range.from')}
                        </label>
                        <input
                          id={startId}
                          type="date"
                          className="border rounded-md px-2 h-9"
                          value={start}
                          onChange={(event) => setStart(event.target.value)}
                        />
                        <span aria-hidden="true">〜</span>
                        <label htmlFor={endId} className="sr-only">
                          {t('export-range.to')}
                        </label>
                        <input
                          id={endId}
                          type="date"
                          className="border rounded-md px-2 h-9"
                          value={end}
                          onChange={(event) => setEnd(event.target.value)}
                        />
                      </div>
                    )}
                    {option('all', t('export-range.all'))}
                  </fieldset>
                  {error && (
                    <p role="alert" className="mx-6 mb-2 text-red-700 text-body-2">
                      {error}
                    </p>
                  )}
                  <div className="flex flex-row justify-end gap-2 px-6 pb-4">
                    <button
                      type="button"
                      className="px-4 h-10 border rounded-md"
                      onClick={close}
                      disabled={busy}
                    >
                      {t('ui.cancel')}
                    </button>
                    <button
                      type="submit"
                      className="px-4 h-10 rounded-md bg-primary/300 text-white"
                      disabled={busy}
                    >
                      {busy ? t('ui.loading') : t('export-range.submit')}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
    </>
  );
};

export default ExportRangeDialog;
